import { Injectable } from "@angular/core";
import { Cirf, CirfStatusType } from "./cirf";
import { CirfClientInfo } from "./cirf-client-info";
import { Observable, Subscription } from "rxjs/index";
import { connectApi } from "../common/connect-api";
import { HttpClient } from "@angular/common/http";
import { debounceTime, map } from "rxjs/internal/operators";
import { CustomHttpParams } from "../common/connect-http-interceptor";
import { CirfMatterContactInfo } from "./cirf-matter-contact-info";
import { SESSION_STORAGE_KEYS } from "../common/session-storage-keys";
import { CirfInsuranceDetail } from "./cirf-insurance-detail";
import { CirfMatterData } from "./cirf-matter-data";
import { CirfMortgage } from "./cirf-mortgage";

import { FormGroup, Validators } from "@angular/forms";
import {
  AllValidationErrors,
  getFormValidationErrors,
} from "../common/get-form-validation-errors";
import { CommonService } from "../common/common.service";
import { CustomPicklist } from "../common/custom-picklist";
import * as _ from "lodash";
import { MatterInfo } from "../common/matter-info";
import {
  CIRF_ADDITIONAL_TOPICS,
  CIRF_CLIENT_TOPICS,
  CIRF_NAME_KEYS,
  CIRF_OTHER_TOPICS,
  CLIENT_SECTIONS_FOR_ERROR_VALIDATION,
  DpBooleanValueTypes,
  CIRF_EXISTING_MORTGAGE_NOTES_TOPICS,
  CIRF_NEW_MORTGAGE_NOTES_TOPICS,
} from "../shared/constants";
import { Utils } from "../common/utils";
import { CirfNavigation, NavigationSection } from "./cirf-navigation";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import {
  ClientNameLookupDialogComponent,
  ClientNameLookupDialogParams,
} from "./cirf-lookup/client-name-lookup-dialog.component";
import { CirfDocument } from "./cirf-document";
import { of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class CirfService {
  private _cirf: Cirf;
  private _cirfFormGroup: FormGroup;
  private _destroyCirfUpdateSubscription: Subscription;
  public cirfNavigation: CirfNavigation;

  public cleanMortgageIds: string[];
  constructor(
    private httpClient: HttpClient,
    private commonService: CommonService,
    public dialog: MatDialog
  ) {}

  get cirf(): Cirf {
    if (!this._cirf) {
      this._cirf = this.getCirfFromSessionStorage();
    }
    return this._cirf;
  }

  get cirfFormGroup(): FormGroup {
    if (!this._cirfFormGroup && !!this._cirf) {
      this._cirfFormGroup = this.commonService.createFormControls(this._cirf);
    }
    return this._cirfFormGroup;
  }

  set cirf(cirf: Cirf) {
    this._cirf = cirf;
  }

  resetCirf(): void {
    this._cirf = undefined;
    this._cirfFormGroup = undefined;
    this.clearCirfSessionStorage();
  }

  get matterType(): string {
    return this.cirf &&
      this.cirf.cirfMatterData &&
      this.cirf.cirfMatterData.isCustomMatter()
      ? this.cirf.cirfMatterData.customMatterTypeDesc
      : this.cirf &&
          this.cirf.cirfMatterData &&
          this.cirf.cirfMatterData.matterType;
  }

  get matterTypeText(): string {
    return this.matterType
      ? this.matterType.toLowerCase().charAt(0).toUpperCase() +
          this.matterType.toLowerCase().slice(1)
      : "";
  }

  get clients(): CirfClientInfo[] {
    return this._cirf ? this._cirf.cirfClients : [];
  }

  get mortgages(): CirfMortgage[] {
    return this._cirf ? this._cirf.mortgages : [];
  }

  get cirfMatterContactInfo(): CirfMatterContactInfo {
    return this._cirf && this._cirf.matterContactInfo;
  }

  get fireInsuranceDetail(): CirfInsuranceDetail {
    return this._cirf && this._cirf.fireInsuranceDetail;
  }

  set fireInsuranceDetail(cirfInsuranceDetail: CirfInsuranceDetail) {
    this._cirf.fireInsuranceDetail = cirfInsuranceDetail;
  }

  get cirfMatterData(): CirfMatterData {
    return this._cirf && this._cirf.cirfMatterData;
  }

  get selectedNavSection(): NavigationSection {
    return (
      this._cirf &&
      this._cirf.cirfMatterData &&
      this._cirf.cirfMatterData.selectedNavSection
    );
  }

  saveMortgage(cirfMortgage: CirfMortgage): void {
    let _cirfMortgageIndex = this.mortgages.findIndex(
      (item) => item.id == cirfMortgage.id
    );
    if (_cirfMortgageIndex > -1) {
      this.mortgages[_cirfMortgageIndex] = cirfMortgage;
    } else {
      this.mortgages.push(cirfMortgage);
    }
  }

  getCirf(connectToken: string): Observable<Cirf> {
    return this.httpClient
      .get<Cirf>(`${connectApi.getCirf}` + connectToken)
      .pipe(
        map((result) => {
          let cirf = new Cirf(result["Cirf"]);
          //if it is existing, we think it accessed all pages
          if (cirf?.cirfMatterData) {
            if (
              cirf.cirfMatterData.selectedNavSection &&
              !cirf.cirfMatterData.accessNavSections
            ) {
              this.buildNavigationSections();
              cirf.cirfMatterData.accessNavSections =
                this.cirfNavigation?.navigationSections.slice();
            }
          }
          return cirf;
        })
      );
  }

  saveCirf(
    cirf: Cirf,
    connectToken: string,
    isSubmit?: boolean
  ): Observable<Cirf> {
    let url = connectApi.updateCirf + connectToken;
    if (isSubmit) {
      url += "?submit=true";
    }
    return this.httpClient
      .put<Cirf>(url, cirf, {
        params: new CustomHttpParams(true),
      })
      .pipe(map((result) => new Cirf(result["Cirf"])));
  }

  async updateCirf(status: CirfStatusType) {
    if (this.cirfFormGroup) {
      let connectToken = sessionStorage.getItem(
        SESSION_STORAGE_KEYS.connectToken
      );
      /* getRawValue()
       * Retrieves all values regardless of disabled status.
       * The `value` property only gets the value of the group, and it excludes disabled controls in the `FormGroup`.
       */
      let accessNavSectionList: NavigationSection[] =
        this.cirf?.cirfMatterData?.accessNavSections.slice();
      //let cirf = new Cirf(this.cirfFormGroup.value);
      const cirf = new Cirf(this.cirfFormGroup.getRawValue());
      // make it available before saving to backend for other components that might need it before the update from server returns.
      this.cirf = cirf;
      this.convertPartialDateToSave(cirf);
      this.updateSpouseNames(cirf);
      this.saveSelectedNav(cirf);
      this.saveAccessNavSectionList(cirf, accessNavSectionList);
      this.checkErrors(cirf);
      if (cirf.isOutstanding()) {
        cirf.status = status;
      }
      cirf.cleanUpCirf();
      this.cirf = await this.saveCirf(cirf, connectToken).toPromise();
      this.saveCirfToSessionStorage(this.cirf);
    }
    return this.cirf;
  }

  saveAccessNavSectionList(
    cirf: Cirf,
    accessNavSectionList: NavigationSection[]
  ) {
    if (this.cirf?.cirfMatterData) {
      // to avoid lose accessNavSectionList.
      this.cirf.cirfMatterData.accessNavSections = accessNavSectionList;
    }
  }

  saveSelectedNav(cirf: Cirf): void {
    // Save the last visited section
    if (cirf && cirf.cirfMatterData && this.cirfNavigation) {
      cirf.cirfMatterData.selectedNavSection = new NavigationSection();
      cirf.cirfMatterData.selectedNavSection.clientIndex =
        this.cirfNavigation.selectedClientIndex;
      cirf.cirfMatterData.selectedNavSection.topicKey =
        this.cirfNavigation.selectedTopicKey;
      cirf.cirfMatterData.selectedNavSection.sectionKey =
        this.cirfNavigation.selectedSectionKey;
      cirf.cirfMatterData.selectedNavSection.newMortgageIndex =
        this.cirfNavigation.selectedNewMortgageIndex;
      cirf.cirfMatterData.selectedNavSection.existingMortgageIndex =
        this.cirfNavigation.selectedExistingMortgageIndex;
    }
  }

  convertPartialDateToSave(cirf: Cirf): void {
    if (
      cirf &&
      cirf.cirfMatterData &&
      cirf.cirfMatterData.cirfClients &&
      cirf.cirfMatterData.cirfClients.length
    ) {
      cirf.cirfMatterData.cirfClients.forEach((client) => {
        if (client.identifications && client.identifications.length) {
          client.identifications.forEach((identification) => {
            identification.expiryDate =
              this.commonService.convertPartialDateToSave(
                identification.expiryDate
              );
          });
        }
      });
    }
  }

  saveCirfToSessionStorage(cirf: Cirf): void {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.cirf, JSON.stringify(cirf));
  }

  clearCirfSessionStorage(): void {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.cirf);
  }

  getCirfFromSessionStorage(): Cirf {
    let cirf: string = sessionStorage.getItem(SESSION_STORAGE_KEYS.cirf);
    return !!cirf ? new Cirf(JSON.parse(cirf)) : undefined;
  }

  setUpFormControl(object: any): void {
    this._cirfFormGroup = this.commonService.createFormControls(object);
  }

  saveChangesOnCirfFormChanges(): void {
    if (this._destroyCirfUpdateSubscription) {
      this._destroyCirfUpdateSubscription.unsubscribe();
    }
    this._destroyCirfUpdateSubscription = this.cirfFormGroup.valueChanges
      .pipe(debounceTime(2000))
      .subscribe(() => {
        this.updateCirf("INPROGRESS");
      });
  }

  validateClientSections(
    clientControl: FormGroup,
    errors: AllValidationErrors[],
    cirf: Cirf,
    index: number
  ): void {
    if (index >= 0 && cirf.cirfMatterData.cirfClients[index]) {
      let cirfClient = cirf.cirfMatterData.cirfClients[index];
      for (let section of CLIENT_SECTIONS_FOR_ERROR_VALIDATION) {
        cirfClient[section.sectionErrorField] = false;
        for (let error of errors) {
          if (section.sectionList.includes(error["control_name"])) {
            cirfClient[section.sectionErrorField] = true;
            break;
          }
        }
      }
      if (!cirfClient.hasContactDetailsSectionErrors) {
        //Check the UDFs
        let clientInfoUdf =
          clientControl.controls.cirfClientInfoUdfs["controls"];
        for (let formGroup of clientInfoUdf) {
          let clientInfoUdfErrors: AllValidationErrors[] =
            getFormValidationErrors(formGroup.controls);
          if (clientInfoUdfErrors && clientInfoUdfErrors.length) {
            cirfClient.hasContactDetailsSectionErrors = true;
            break;
          }
        }
      }
      let primaryIdentification =
        clientControl.controls.identifications["controls"][0];
      let primaryIdentificationErrors: AllValidationErrors[] =
        getFormValidationErrors(primaryIdentification.controls);
      console.log(primaryIdentificationErrors);
      cirfClient.hasIdVerification1SectionErrors =
        primaryIdentificationErrors.length > 0;

      let secondaryIdentification =
        clientControl.controls.identifications["controls"][1];
      let secondaryIdentificationErrors: AllValidationErrors[] =
        getFormValidationErrors(secondaryIdentification.controls);
      console.log(secondaryIdentificationErrors);
      cirfClient.hasIdVerification2SectionErrors =
        secondaryIdentificationErrors.length > 0;
    }
  }

  checkErrors(cirf: Cirf): void {
    this.cirfFormGroup
      .get("cirfMatterData")
      ["controls"].cirfClients.controls.forEach((item, index) => {
        Utils.updateFormGroupRequiredAttributesForClient(
          item,
          !item.value.allowMissingFields,
          Utils.EMAIL_ADDRESS_PATTERN,
          true
        );
        Utils.updatePhoneValidationForClient(
          item,
          !item.value.allowMissingFields,
          true
        );
        let errors: AllValidationErrors[] = getFormValidationErrors(
          item.controls
        );
        item.value.hasErrors = errors.length > 0;
        this.validateClientSections(item, errors, cirf, index);
        /*
         * As cirf is now constructed from this.cirfFormGroup.getRawValue()
         * Changing the item.value.hasErrors will not change it in the cirf object
         * So we have to manually set hasErrors flag in the cirf object
         */
        cirf.cirfMatterData.cirfClients[index].hasErrors = errors.length > 0;

        if (
          this.isMandatorySpouseMissing(item.value) &&
          !cirf.cirfMatterData.cirfClients[index].allowMissingFields
        ) {
          item.value.hasErrors = true;
          cirf.cirfMatterData.cirfClients[index].hasErrors = true;
          cirf.cirfMatterData.cirfClients[
            index
          ].hasContactDetailsSectionErrors = true;
        }
      });

    if (this.cirf?.isMatterCirf() && !this.cirf?.isCustomMatter()) {
      const propertyFormGroup =
        this.cirfFormGroup.get("cirfMatterData")["controls"].matterContactInfo;
      this.updateClosingAddressValidations(propertyFormGroup);
      const errors: AllValidationErrors[] = getFormValidationErrors(
        propertyFormGroup.controls
      );
      propertyFormGroup.value.hasErrors = errors.length > 0;
      cirf.cirfMatterData.matterContactInfo.hasErrors = errors.length > 0;

      const insuranceFormGroup =
        this.cirfFormGroup.get("cirfMatterData")["controls"]
          .fireInsuranceDetail;
      let insuranceErrors: AllValidationErrors[] = getFormValidationErrors(
        insuranceFormGroup.controls
      );
      insuranceFormGroup.value.hasErrors = insuranceErrors.length > 0;
      cirf.cirfMatterData.fireInsuranceDetail.hasErrors =
        insuranceErrors.length > 0;

      this.cirfFormGroup
        .get("cirfMatterData")
        ["controls"].mortgages.controls.forEach((item, index) => {
          let errors: AllValidationErrors[] = getFormValidationErrors(
            item.controls
          );
          item.value.hasErrors = errors.length > 0;
          cirf.cirfMatterData.mortgages[index].hasErrors = errors.length > 0;
        });
    }
  }

  updateClosingAddressValidations(formGroup: FormGroup): void {
    // clean the other address (not visible) validators
    // [formGroupName]="cirf?.isMatterPurchase() ? 'preClosingAddress' : 'postClosingAddress'"
    const otherClosingAddress = this.cirf?.isMatterPurchase()
      ? "postClosingAddress"
      : "preClosingAddress";
    if (otherClosingAddress) {
      [
        "addressLine1",
        "addressLine2",
        "city",
        "provinceCode",
        "postalCode",
        "country",
      ].forEach((key) => {
        const control =
          formGroup && formGroup.get(otherClosingAddress).get(key);
        if (control) {
          control.clearValidators();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
    if (this.cirf?.isMatterMortgage()) {
      const isRequired =
        this.cirf?.cirfMatterData.matterContactInfo
          .residingAtSubjectProperty === "NO";
      ["postClosingAddress"].forEach((parentKey) => {
        [
          "addressLine1",
          "addressLine2",
          "city",
          "provinceCode",
          "postalCode",
          "country",
        ].forEach((key) => {
          const control = formGroup && formGroup.get(parentKey).get(key);
          if (control) {
            if (
              isRequired &&
              this.commonService.isFieldMandatory(
                `matterContactInfo.address.${key}`
              )
            ) {
              control.setValidators([Validators.required]);
            } else {
              control.clearValidators();
            }
            control.updateValueAndValidity({
              onlySelf: true,
            });
          }
        });
      });
    }
  }

  isMandatorySpouseMissing(clientFormValue: any): boolean {
    return (
      clientFormValue.isSpouseOnTitle == DpBooleanValueTypes.YES &&
      clientFormValue.spousalStatus == "MARRIED" &&
      !clientFormValue.spouseId &&
      this.cirf?.isMatterCirf()
    );
  }

  getCustomePickList(
    cirfGuid: string,
    type: string
  ): Observable<CustomPicklist[]> {
    let url = connectApi.customPickLists.replace("{cirfGuid}", cirfGuid);
    return this.httpClient
      .get(`${url}?type=${type}`, { params: new CustomHttpParams(true) })
      .pipe(
        map((result) => {
          let customePicklist: CustomPicklist[] = [];
          let data: any[] = result["CUSTOMPICKLISTS"];
          let responseSorted = _.orderBy(
            data,
            ["customPickListItemSequence"],
            ["asc"]
          );
          responseSorted.forEach((item) => {
            customePicklist.push(new CustomPicklist(item));
          });
          return customePicklist;
        })
      );
  }

  getCirfTransactionsForCurrentUser(): Observable<Cirf[]> {
    return this.httpClient
      .get(connectApi.getCirfsForCurrentUser, {
        params: new CustomHttpParams(true),
      })
      .pipe(map((res) => res["Cirfs"]));
  }

  getCirfMatterInfo(guid: string): Observable<MatterInfo> {
    return this.httpClient
      .get<MatterInfo>(
        `${connectApi.getCirfMatterInfo.replace("{cirfGuid}", guid)}`
      )
      .pipe(map((result) => new MatterInfo(result["MatterInfo"])));
  }

  getMatterInfoByMatterId(matterId: string): Observable<MatterInfo> {
    return this.httpClient
      .get<MatterInfo>(
        `${connectApi.getMatterInfoByMatterId.replace("{matterId}", matterId)}`
      )
      .pipe(map((result) => new MatterInfo(result["MatterInfo"])));
  }

  getClient(id: string): CirfClientInfo {
    return this.clients.find((client) => client.id == id);
  }

  getCirfLogoFirm(id: string, fileId: number): string {
    return connectApi.getCirfFirmLogo
      .replace("{cirfGuid}", id)
      .replace("{fileId}", fileId.toString())
      .replace(
        "{sessionId}",
        sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId)
      );
  }

  private updateSpouseNames(cirf: Cirf) {
    if (cirf) {
      cirf.cirfClients
        .filter((cirfClient) => cirfClient.spouseId)
        .forEach((cirfClient) => {
          const spouseClient = cirf.cirfClients.find(
            (client) => client.spouseId === cirfClient.id
          );
          if (spouseClient) {
            cirfClient.spouseName = spouseClient.fullName;
          }
        });
    }
  }

  buildNavigationSections(): void {
    if (this.cirfNavigation) {
      this.cirfNavigation.navigationSections = [];
      this.clients.forEach((client, index) => {
        CIRF_CLIENT_TOPICS.forEach((topic) => {
          topic.topicSections.forEach((section) => {
            let navigationSection = this.buildNavigationSection(
              index,
              topic.topicKey,
              section.sectionKey,
              -1,
              -1
            );
            this.cirfNavigation.navigationSections.push(navigationSection);
          });
        });
      });

      CIRF_OTHER_TOPICS.forEach((topic) => {
        topic.topicSections.forEach((section) => {
          let index =
            section.sectionKey == CIRF_NAME_KEYS.clientNotes.key ? 0 : -1;
          if (this.isSectionVisible(section)) {
            let navigationSection = this.buildNavigationSection(
              index,
              topic.topicKey,
              section.sectionKey,
              -1,
              -1
            );
            this.cirfNavigation.navigationSections.push(navigationSection);
          }
        });
      });

      if (this.cirf?.cirfMatterData?.newMortgages?.length) {
        this.cirf.cirfMatterData.newMortgages.forEach((mortgage, index) => {
          let navigationSection = this.buildNavigationSection(
            -1,
            null,
            null,
            index,
            -1
          );
          this.cirfNavigation.navigationSections.push(navigationSection);
        });
      } else {
        if (
          (this.cirf?.isMatterPurchase() || this.cirf?.isMatterMortgage()) &&
          this.cirf?.isMatterCirf() &&
          !this.cirf?.isCustomMatter()
        ) {
          let navigationSection = this.buildNavigationSection(
            -1,
            null,
            null,
            0,
            -1
          );
          this.cirfNavigation.navigationSections.push(navigationSection);
        }
      }

      //Mortgage Notes
      if (
        this.cirf?.isMatterCirf() &&
        (this.cirf?.isMatterPurchase() || this.cirf?.isMatterMortgage()) &&
        !this.cirf?.isCustomMatter()
      ) {
        CIRF_NEW_MORTGAGE_NOTES_TOPICS.forEach((topic) => {
          topic.topicSections.forEach((section) => {
            let navigationSection = this.buildNavigationSection(
              -1,
              topic.topicKey,
              section.sectionKey,
              -1,
              -1
            );
            this.cirfNavigation.navigationSections.push(navigationSection);
          });
        });
      }

      if (this.cirf?.cirfMatterData?.existingMortgage?.length) {
        this.cirf.cirfMatterData.existingMortgage.forEach((mortgage, index) => {
          let navigationSection = this.buildNavigationSection(
            -1,
            null,
            null,
            -1,
            index
          );
          this.cirfNavigation.navigationSections.push(navigationSection);
        });
      } else {
        if (
          this.cirf?.isMatterCirf() &&
          (this.cirf?.isMatterSale() || this.cirf?.isMatterMortgage())
        ) {
          let navigationSection = this.buildNavigationSection(
            -1,
            null,
            null,
            -1,
            0
          );
          this.cirfNavigation.navigationSections.push(navigationSection);
        }
      }
      //Existing Mortgage Notes
      if (
        this.cirf?.isMatterCirf() &&
        (this.cirf?.isMatterSale() || this.cirf?.isMatterMortgage())
      ) {
        CIRF_EXISTING_MORTGAGE_NOTES_TOPICS.forEach((topic) => {
          topic.topicSections.forEach((section) => {
            let navigationSection = this.buildNavigationSection(
              -1,
              topic.topicKey,
              section.sectionKey,
              -1,
              -1
            );
            this.cirfNavigation.navigationSections.push(navigationSection);
          });
        });
      }

      CIRF_ADDITIONAL_TOPICS.forEach((topic) => {
        topic.topicSections.forEach((section) => {
          if (this.isSectionVisible(section)) {
            let navigationSection = this.buildNavigationSection(
              -1,
              topic.topicKey,
              section.sectionKey,
              -1,
              -1
            );
            this.cirfNavigation.navigationSections.push(navigationSection);
          }
        });
      });
      console.log(this.cirfNavigation.navigationSections);
    }
  }

  buildNavigationSection(
    clientIndex: number,
    topicKey: string,
    sectionKey: string,
    newMortgageIndex: number,
    existingMortgageIndex: number
  ): NavigationSection {
    let navigationSection = new NavigationSection();
    navigationSection.clientIndex = clientIndex;
    navigationSection.topicKey = topicKey;
    navigationSection.sectionKey = sectionKey;
    navigationSection.existingMortgageIndex = existingMortgageIndex;
    navigationSection.newMortgageIndex = newMortgageIndex;
    return navigationSection;
  }

  isSectionVisible(section): boolean {
    return !(
      section.sectionKey == CIRF_NAME_KEYS.newInsuranceDetails.key &&
      !this.isInsuranceApplicable()
    );
  }

  isInsuranceApplicable(): boolean {
    return this.cirf && this.cirf.isInsuranceApplicable();
  }

  createMortgageAction(mortgageType: string): CirfMortgage {
    let mortgage = new CirfMortgage();
    mortgage.id = Utils.uuid();
    mortgage.mortgageType = mortgageType;
    this.saveMortgage(mortgage);
    this.setUpFormControl(this.cirf);
    return mortgage;
  }

  openClientNameLookupDialogAction(
    params: ClientNameLookupDialogParams
  ): Observable<any> {
    if (params) {
      const matDialogConfig = new MatDialogConfig();
      matDialogConfig.maxWidth = "900px";
      matDialogConfig.maxHeight = "9000px";
      matDialogConfig.data = params;
      const dialogRef = this.dialog.open(
        ClientNameLookupDialogComponent,
        matDialogConfig
      );
      return dialogRef.afterClosed();
    } else {
      console.error("No params provided for ClientNameLookupDialog");
    }
  }

  openClientNameLookupDialog(
    params: ClientNameLookupDialogParams
  ): Observable<any> {
    let cirfClientNew: CirfClientInfo;
    if (params && params.cirfClient) {
      cirfClientNew.firstName =
        params.cirfClient && params.cirfClient.firstName
          ? params.cirfClient.firstName
          : "";
      cirfClientNew.lastName =
        params.cirfClient && params.cirfClient.lastName
          ? params.cirfClient.lastName
          : "";
      let clientNameNotFound =
        !cirfClientNew.firstName ||
        cirfClientNew.firstName.trim() == "" ||
        !cirfClientNew.lastName ||
        cirfClientNew.lastName.trim() == "";
      if (!clientNameNotFound) {
        // this.searchLoading = true;
        this.getLookupCirfClient(
          cirfClientNew.firstName,
          cirfClientNew.lastName
        ).subscribe((cirf: Cirf) => {
          if (cirf && cirf.cirfClients && cirf.cirfClients.length > 0) {
            let cirfClient = cirf.cirfClients.find(
              (client) =>
                client.firstName &&
                client.firstName.toLowerCase() ==
                  cirfClientNew.firstName.toLowerCase() &&
                client.lastName &&
                client.lastName.toLowerCase() ==
                  cirfClientNew.lastName.toLowerCase()
            );
            if (cirfClient) {
              params.cirf = cirf;
              return this.openClientNameLookupDialogAction(params);
            }
          }
        });
      }
    } else {
      return this.openClientNameLookupDialogAction(params);
    }
    return of(null);
  }

  getLookupCirfClient(firstName: string, lastName: string): Observable<Cirf> {
    let url = `${connectApi.lookUpCirf}`;
    url += `?firstName=${firstName}&lastName=${lastName}`;
    return this.httpClient
      .get<Cirf>(url)
      .pipe(map((result) => new Cirf(result["Cirf"])));
  }

  copyCirfClientDocuments(
    cirfGuid: string,
    clientId: string,
    fromCirfGuid: string,
    fromClientId: string
  ): Observable<CirfDocument[]> {
    let url = `${connectApi.copyCirfClientDocuments
      .replace("{cirfGuid}", cirfGuid)
      .replace("{clientId}", clientId)
      .replace("{fromCirfGuid}", fromCirfGuid)
      .replace("{fromClientId}", fromClientId)}`;
    return this.httpClient.post<any>(url, {}).pipe(
      map((results) =>
        results["CirfDocuments"].map((doc) => {
          return new CirfDocument(doc);
        })
      )
    );
  }
}
